/**
 * <cart-drawer>
 *
 * Slide-in cart panel + global cart AJAX layer.
 *
 * Responsibilities:
 *   - Open/close (click on [data-cart-toggle] anywhere; [data-cart-close]
 *     inside; Escape; backdrop). Focus trap inside the panel via Tab handler;
 *     uses the `inert` attribute on close so the panel is fully removed from
 *     the focus tree while off-screen.
 *   - Cart mutations (add via cart:added event, quantity +/−, remove): POSTs
 *     to /cart/change.js, then refreshes UI via the Section Rendering API
 *     (?sections=cart-drawer,main-cart). Same JS drives the /cart page items.
 *   - Cart count badges: any element with [data-cart-count] gets its visible
 *     number updated and toggled hidden based on cart.item_count.
 */

const SECTIONS_TO_REFRESH = 'cart-drawer,main-cart';

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-cart-panel]');
    this.body = this.querySelector('[data-cart-body]');
    this.footer = this.querySelector('[data-cart-footer]');
    this.errorEl = this.querySelector('[data-cart-error]');
    // Capture the server-rendered generic fallback so we can revert to it
    // after showing a request-specific message from Shopify.
    this._defaultErrorMessage = this.errorEl ? this.errorEl.textContent.trim() : '';
    this.previousFocus = null;
    this.isOpen = false;

    // Event delegation at the document level so a single instance handles
    // toggles, closes, cart-item buttons inside the drawer, AND cart-item
    // buttons on the /cart page.
    document.addEventListener('click', this._onDocumentClick.bind(this));
    document.addEventListener('keydown', this._onDocumentKeydown.bind(this));
    document.addEventListener('cart:added', this._onCartAdded.bind(this));
  }

  _onDocumentClick(e) {
    const toggle = e.target.closest('[data-cart-toggle]');
    if (toggle) {
      e.preventDefault();
      this.open(toggle);
      return;
    }

    const close = e.target.closest('[data-cart-close]');
    if (close) {
      e.preventDefault();
      this.close();
      return;
    }

    const inc = e.target.closest('[data-cart-increase]');
    const dec = e.target.closest('[data-cart-decrease]');
    const rem = e.target.closest('[data-cart-remove]');

    if (inc) {
      e.preventDefault();
      this._change(inc.dataset.cartIncrease, this._currentQty(inc) + 1);
    } else if (dec) {
      e.preventDefault();
      this._change(dec.dataset.cartDecrease, this._currentQty(dec) - 1);
    } else if (rem) {
      e.preventDefault();
      this._change(rem.dataset.cartRemove, 0);
    }
  }

  _onDocumentKeydown(e) {
    if (!this.isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
    } else if (e.key === 'Tab') {
      this._trapFocus(e);
    }
  }

  async _onCartAdded() {
    await this.refresh();
    this.open();
  }

  open(trigger) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.previousFocus = trigger || document.activeElement;
    this.classList.add('is-open');
    if (this.panel) {
      this.panel.removeAttribute('inert');
      this.panel.setAttribute('aria-hidden', 'false');
    }
    document.body.style.overflow = 'hidden';
    const first = this._firstFocusable();
    if (first) first.focus();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.classList.remove('is-open');
    if (this.panel) {
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.setAttribute('inert', '');
    }
    document.body.style.overflow = '';
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }
  }

  _currentQty(btn) {
    const item = btn.closest('[data-cart-item-key]');
    const display = item && item.querySelector('[data-qty-display]');
    return display ? parseInt(display.textContent, 10) || 0 : 0;
  }

  async _change(key, quantity) {
    this._hideError();
    const items = document.querySelectorAll(`[data-cart-item-key="${key}"]`);
    items.forEach((el) => el.classList.add('is-updating'));

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ id: key, quantity: quantity }),
      });
      if (!res.ok) {
        // Shopify's Ajax Cart returns JSON like
        //   { status: 422, message: "Cart Error",
        //     description: "You can only add 1 of this item to your cart." }
        // on inventory/cap errors. Surface the `description` (most
        // user-friendly), fall back to `message`, then to the generic.
        let detail = null;
        try {
          const body = await res.json();
          detail = body.description || body.message || null;
        } catch (_) {
          /* response wasn't JSON — fall through to generic */
        }
        throw new Error(detail || 'Cart change failed (' + res.status + ')');
      }
      await this.refresh();
    } catch (err) {
      console.error('Cart change error:', err);
      items.forEach((el) => {
        el.classList.remove('is-updating');
        el.classList.add('has-error');
      });
      this._showError(err && err.message);
    }
  }

  async refresh() {
    try {
      const [sectionsRes, cartRes] = await Promise.all([
        fetch('/?sections=' + SECTIONS_TO_REFRESH, {
          headers: { 'Accept': 'application/json' },
        }),
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } }),
      ]);
      const sections = await sectionsRes.json();
      const cart = await cartRes.json();

      this._applyDrawerSection(sections['cart-drawer']);
      this._applyMainCartSection(sections['main-cart']);
      this._updateCountBadges(cart.item_count);
    } catch (err) {
      console.error('Cart refresh error:', err);
      this._showError();
    }
  }

  _applyDrawerSection(html) {
    if (!html || !this.body || !this.footer) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html');
    const newBody = fresh.querySelector('[data-cart-body]');
    const newFooter = fresh.querySelector('[data-cart-footer]');
    if (newBody) this.body.innerHTML = newBody.innerHTML;
    if (newFooter) {
      this.footer.innerHTML = newFooter.innerHTML;
      if (newFooter.hasAttribute('hidden')) this.footer.setAttribute('hidden', '');
      else this.footer.removeAttribute('hidden');
    }
  }

  _applyMainCartSection(html) {
    // /cart page rewrites both the items column and (when non-empty) the
    // summary's subtotal. If the user isn't on /cart, the matching nodes
    // don't exist here — these selectors no-op gracefully.
    if (!html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html');

    const currentItems = document.querySelector('[data-cart-page-items]');
    const newItems = fresh.querySelector('[data-cart-page-items]');
    if (currentItems && newItems) currentItems.innerHTML = newItems.innerHTML;

    const newSummary = fresh.querySelector('[data-cart-page-summary]');
    const currentSummary = document.querySelector('[data-cart-page-summary]');
    if (currentSummary && newSummary) {
      currentSummary.innerHTML = newSummary.innerHTML;
    } else if (currentSummary && !newSummary) {
      // Cart became empty — drop the summary entirely.
      currentSummary.remove();
    }
  }

  _updateCountBadges(count) {
    const badges = document.querySelectorAll('[data-cart-count]');
    badges.forEach((badge) => {
      const num = badge.querySelector('[aria-hidden="true"]') || badge;
      num.textContent = count;
      if (count > 0) badge.removeAttribute('hidden');
      else badge.setAttribute('hidden', '');
    });
  }

  _showError(message) {
    if (!this.errorEl) return;
    // Use the specific message when we got one from Shopify; otherwise
    // restore the server-rendered generic fallback we captured on init.
    this.errorEl.textContent = (message && String(message).trim()) || this._defaultErrorMessage;
    this.errorEl.removeAttribute('hidden');
    clearTimeout(this._errorTimer);
    this._errorTimer = setTimeout(() => this._hideError(), 5000);
  }
  _hideError() {
    if (!this.errorEl) return;
    this.errorEl.setAttribute('hidden', '');
    // Reset to the generic text so the next error doesn't briefly show the
    // stale specific message before being overwritten.
    this.errorEl.textContent = this._defaultErrorMessage;
  }

  _firstFocusable() {
    return (
      this.querySelector('[data-cart-close]') ||
      this.querySelector('a[href], button:not([disabled])')
    );
  }

  _focusables() {
    if (!this.panel) return [];
    return Array.from(
      this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);
  }

  _trapFocus(e) {
    const f = this._focusables();
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
